import { FC } from 'react';

import { useFormik } from 'formik';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';

import { LotteryForm, initialValues } from '../form';

import css from './AddLotteryForm.module.css';

import { lotteriesService } from 'v2/api/lotteriesService';
import { MarkedAutocomplete } from 'v2/components/autocomplete/markedAutocomplete/MarkedAutocomplete';
import { Card } from 'v2/components/card/Card';
import { CardHeader } from 'v2/components/card/cardHeader/CardHeader';
import { Switch } from 'v2/components/switch/Switch';
import { handleHttpError } from 'v2/utils';

interface AddLotteryFormProps {
  onSuccess: VoidFunction;
}

export const AddLotteryForm: FC<AddLotteryFormProps> = ({ onSuccess }) => {
  const formik = useFormik<LotteryForm>({
    initialValues,
    onSubmit: (values) => {
      trigger(values);
    },
  });

  const { trigger, isMutating } = useSWRMutation('createLottery', lotteriesService.createLottery, {
    onSuccess: () => {
      onSuccess();
      toast.success('The lottery has been successfully created');
      formik.resetForm({ values: initialValues });
    },
    onError: handleHttpError,
  });

  const handleAutocompleteChange = (key: keyof LotteryForm) => (v: string[]) => {
    formik.setFieldValue(key, v);
  };

  return (
    <Card className={css.addLotteryFormContainer}>
      <CardHeader title="Create New Lottery" icon={Plus} />

      <form className="form" onSubmit={formik.handleSubmit}>
        <MarkedAutocomplete
          label="Handled Participants"
          value={formik.values.handledParticipants}
          options={formik.values.handledParticipants}
          onChange={handleAutocompleteChange('handledParticipants')}
          required
        />

        <MarkedAutocomplete
          label="Verified Participants"
          value={formik.values.verifiedParticipants}
          options={formik.values.verifiedParticipants}
          onChange={handleAutocompleteChange('verifiedParticipants')}
          required
        />

        <MarkedAutocomplete
          label="RS Referrals"
          value={formik.values.realSlotsReferrals}
          options={formik.values.realSlotsReferrals}
          onChange={handleAutocompleteChange('realSlotsReferrals')}
          required
        />

        <Switch name="isActive" checked={formik.values.isActive} onChange={formik.handleChange} label="Active" />

        <Button type="submit" loading={isMutating} variant="contained" size="large">
          Create
        </Button>
      </form>
    </Card>
  );
};
